// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/finance/journal/JournalReport.tsx   (روی فایل قبلی بنویسید)
//
// گزارش جامع ژورنال — همه‌ی ستون‌های هر ۷ تراکنش، کارت‌های خلاصه، جمع هر ارز به‌تفکیک،
// بخش «مفاد و ضرر تبادله»، خروجی PDF و Excel لوکس با همان انتخاب دوره
// ============================================================

import { useState, useCallback, useMemo } from "react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { useTheme, DARK, LIGHT } from "../../../components/Layout";
import { journalAPI, accountsAPI } from "../../../services/api";
import PersianDatePicker, { todayGregorian } from "../../../utils/PersianDatePicker";
import { FileText, FileSpreadsheet, Calendar, Loader2, ChevronDown, ChevronUp, RefreshCw } from "lucide-react";
import {
  buildSpec, addSpecSheet, specHtml, openPdfWindow, RSpec, RCol,
  currenciesOf, stat, jal, nf, nf2, esc, fileUrl, nowJalali, nowTime,
} from "../../../utils/txnReport";

const SECTIONS: { key: string; type: string; rep: string; label: string; color: string; icon: string }[] = [
  { key: "purchases",   type: "purchases",    rep: "purchases",   label: "خریدها",        color: "D97706", icon: "🛒" },
  { key: "sales",       type: "sales",        rep: "sales",       label: "فروش‌ها",       color: "059669", icon: "💰" },
  { key: "paymentsIn",  type: "payments_in",  rep: "paymentsIn",  label: "دریافت پول",    color: "2563EB", icon: "📥" },
  { key: "paymentsOut", type: "payments_out", rep: "paymentsOut", label: "پرداخت پول",    color: "DC2626", icon: "📤" },
  { key: "expenses",    type: "expenses",     rep: "expenses",    label: "ثبت مصارف",     color: "7C3AED", icon: "🧾" },
  { key: "cogs",        type: "cogs",         rep: "cogs",        label: "هزینه اجناس",   color: "0F766E", icon: "📦" },
  { key: "transfers",   type: "transfers",    rep: "transfers",   label: "انتقال پول",    color: "0EA5E9", icon: "🔁" },
];

const addDaysStr = (dateStr: string, days: number) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, (d || 1) + days));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
};

interface SumRow { section: string; metric: string; cur: string; n: number; sum: number; color: string }

// حساب‌های نقدی (دخل) — همان منطق قبلی: موجودی بعد از آخرین تراکنشِ تا پایان دوره
const CASH_ACCOUNTS = [
  { code: "CA-1001-03", nameHint: "دخل-دالری", label: "دخل دالری" },
  { code: "CA-2001-02", nameHint: "دخل-افغانی", label: "دخل افغانی" },
];
const findCashAccount = (accounts: any[], code: string, nameHint: string) =>
  (Array.isArray(accounts) ? accounts : []).find((a: any) => (a.code || a.account_code) === code) ||
  (Array.isArray(accounts) ? accounts : []).find((a: any) => String(a.name || a.account_name || "").includes(nameHint)) || null;
const sumDirectional = (txns: any[]) => {
  const list = Array.isArray(txns) ? txns : [];
  return {
    totalIn: list.filter((t: any) => t.direction === "in").reduce((x: number, t: any) => x + Math.abs(parseFloat(t.amount || 0)), 0),
    totalOut: list.filter((t: any) => t.direction === "out").reduce((x: number, t: any) => x + Math.abs(parseFloat(t.amount || 0)), 0),
  };
};

function summaryRows(sec: typeof SECTIONS[number], spec: RSpec): SumRow[] {
  const out: SumRow[] = [];
  const curs = currenciesOf(spec);
  spec.cards.forEach(cd => {
    const col = spec.cols.find(c => c.key === cd.key);
    if (!col || !col.sum) return;
    if (col.sum.cur === "") {
      const s2 = stat(spec, col, null);
      if (s2) out.push({ section: sec.label, metric: cd.label, cur: "", n: s2.n, sum: s2.sum, color: sec.color });
    } else {
      (cd.cur ? [cd.cur] : curs).forEach(cu => {
        const s2 = stat(spec, col, cu);
        if (s2) out.push({ section: sec.label, metric: cd.label, cur: cu, n: s2.n, sum: s2.sum, color: sec.color });
      });
    }
  });
  // میانگین فقط برای قیمت واحد (خرید و فروش)
  spec.cols.filter(c => c.showAvg && c.sum).forEach(col => curs.forEach(cu => {
    const s2 = stat(spec, col, cu);
    if (s2) out.push({ section: sec.label, metric: "میانگین " + col.header, cur: cu, n: s2.n, sum: s2.avg, color: sec.color });
  }));
  return out;
}

export default function JournalReport() {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;

  const [mode, setMode] = useState<"today" | "yesterday" | "all" | "custom">("today");
  const [dateFrom, setDateFrom] = useState(todayGregorian());
  const [dateTo, setDateTo] = useState(todayGregorian());
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<"" | "pdf" | "xls">("");
  const [err, setErr] = useState("");
  const [data, setData] = useState<any>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [cash, setCash] = useState<any[]>([]);

  const getRange = useCallback(() => {
    if (mode === "today") return { from: todayGregorian(), to: todayGregorian(), all: false };
    if (mode === "yesterday") { const y = addDaysStr(todayGregorian(), -1); return { from: y, to: y, all: false }; }
    if (mode === "all") return { from: "", to: "", all: true };
    return { from: dateFrom, to: dateTo, all: false };
  }, [mode, dateFrom, dateTo]);

  const periodLabel = useMemo(() => {
    const r = data?.range;
    if (!r) return "";
    if (r.all) return "همه‌ی دوره‌ها";
    return r.from === r.to ? jal(r.from) : `${r.from ? jal(r.from) : "ابتدا"} تا ${r.to ? jal(r.to) : "امروز"}`;
  }, [data]);

  const load = useCallback(async () => {
    setLoading(true); setErr(""); setData(null);
    const range = getRange();
    try {
      const res = await journalAPI.journalReportFull(range.all ? { time_mode: "all" } : { time_mode: "range", date_from: range.from, date_to: range.to });
      if (!res || !res.success) { setErr(res?.message || "خطا در دریافت گزارش"); setLoading(false); return; }
      setData({ range, rep: res.report || {}, fx: res.fx_summary || [] });
      // موجودی دخل‌ها بعد از آخرین تراکنشِ دوره
      try {
        const accRes: any = await accountsAPI.list().catch(() => null);
        const accList = accRes && accRes.success ? (accRes.accounts || accRes.data || []) : [];
        const today = todayGregorian();
        const endDay = range.all || !range.to ? today : range.to;
        const sums = await Promise.all(CASH_ACCOUNTS.map(async (ca) => {
          const acc = findCashAccount(accList, ca.code, ca.nameHint);
          if (!acc) return { ...ca, found: false };
          const r1: any = await accountsAPI.report(acc.id, endDay, endDay).catch(() => null);
          if (!r1 || !r1.success) return { ...ca, found: false };
          const live = Number(r1.account?.balance || 0);
          let balance = live;
          if (endDay < today) {
            const r2: any = await accountsAPI.report(acc.id, addDaysStr(endDay, 1), today).catch(() => null);
            const after = r2?.success ? sumDirectional(r2.transactions) : { totalIn: 0, totalOut: 0 };
            balance = live - (after.totalIn - after.totalOut);
          }
          return { ...ca, found: true, balance, currency: r1.account?.currency || "" };
        }));
        setCash(sums);
      } catch { setCash(CASH_ACCOUNTS.map(ca => ({ ...ca, found: false }))); }
    } catch { setErr("خطا در ارتباط با سرور"); }
    setLoading(false);
  }, [getRange]);

  const specs = useMemo(() => {
    if (!data) return [] as { sec: typeof SECTIONS[number]; spec: RSpec }[];
    return SECTIONS.map(sec => ({ sec, spec: buildSpec(sec.type, data.rep[sec.rep] || [], { fx: data.fx }) }));
  }, [data]);

  const allSum = useMemo(() => specs.flatMap(({ sec, spec }) => summaryRows(sec, spec)), [specs]);
  const totalCount = specs.reduce((s, x) => s + (x.spec.docCount ?? x.spec.rows.length), 0);
  const meta = { periodLabel, filtersLabel: "" };

  const cashRows = cash.filter(c => c.found);
  // ─────────── PDF ───────────
  const doPdf = () => {
    if (!data) return;
    setBusy("pdf");
    const cards = specs.map(({ sec, spec }) => `<div class="card" style="--c:#${sec.color}"><div class="cl">${sec.icon} ${esc(sec.label)}</div><div class="cv">${nf(spec.docCount ?? spec.rows.length, 0)} <span style="font-size:13px;color:#475569">سند</span></div></div>`).join("");
    const cashHtml = cashRows.length ? `<div class="sec"><div class="tt">🏦 موجودی دخل بعد از آخرین تراکنش</div><div class="cards">${
      cashRows.map(c => `<div class="card" style="--c:#${c.currency === "USD" ? "2563EB" : "059669"};min-width:260px"><div class="cl">${esc(c.label)}</div><div class="cv" style="font-size:28px">${nf2(c.balance)} <span style="font-size:15px">${esc(c.currency)}</span></div></div>`).join("")
    }</div></div>` : "";
    const body = `<div class="cards">${cards}</div>` +
      specs.map(({ spec }) => `<div class="sec">${specHtml(spec, true)}</div>`).join("") + cashHtml;
    const ok = openPdfWindow("گزارش جامع ژورنال", "همه‌ی تراکنش‌های نهایی‌شده", "📒", "1E293B", meta, body, 12);
    if (!ok) setErr("مرورگر پنجره‌ی چاپ را مسدود کرد؛ اجازه‌ی باز شدن پنجره را بدهید.");
    setBusy("");
  };

  // ─────────── Excel ───────────
  const doExcel = async () => {
    if (!data) return;
    setBusy("xls");
    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = "سیستم حسابداری"; wb.created = new Date();
      const NAVY = "1E293B", fill = (argb: string) => ({ type: "pattern" as const, pattern: "solid" as const, fgColor: { argb } });
      const thin = { style: "thin" as const, color: { argb: "CBD5E1" } };
      const border = { top: thin, bottom: thin, left: thin, right: thin };
      const F = (size: number, bold = false, color = "1E293B") => ({ name: "Arial", size, bold, color: { argb: color } });

      const ws = wb.addWorksheet("خلاصه جامع", { views: [{ rightToLeft: true, showGridLines: false }], pageSetup: { orientation: "landscape", fitToPage: true, fitToWidth: 1, fitToHeight: 0 } });
      ws.columns = [{ width: 26 }, { width: 40 }, { width: 14 }, { width: 14 }, { width: 26 }, { width: 22 }];
      ws.mergeCells("A1:F2");
      const t = ws.getCell("A1"); t.value = "📒  گزارش جامع ژورنال";
      t.font = F(26, true, "FFFFFF"); t.alignment = { horizontal: "center", vertical: "middle" };
      for (let r = 1; r <= 2; r++) for (let c = 1; c <= 6; c++) ws.getCell(r, c).fill = fill(NAVY);
      ws.getRow(1).height = 30; ws.getRow(2).height = 30;
      ws.mergeCells("A3:F3");
      const s3 = ws.getCell("A3"); s3.value = `دوره: ${periodLabel}   |   تاریخ تولید: ${nowJalali()}  ${nowTime()}`;
      s3.font = F(14, false, "FFFFFF"); s3.alignment = { horizontal: "center", vertical: "middle" }; s3.fill = fill("2563EB");
      ws.getRow(3).height = 26;

      let r = 5;
      ws.mergeCells(r, 1, r, 6); const h1 = ws.getCell(r, 1); h1.value = "تعداد سند هر بخش";
      h1.font = F(16, true, NAVY); h1.border = { bottom: { style: "medium", color: { argb: "2563EB" } } };
      ws.getRow(r).height = 26; r++;
      const cardRow = (items: typeof specs) => {
        items.forEach(({ sec, spec }, i) => {
          const c = ws.getCell(r, i + 1); c.value = `${sec.icon} ${sec.label}`;
          c.font = F(14, true, "FFFFFF"); c.fill = fill(sec.color); c.alignment = { horizontal: "center", vertical: "middle", wrapText: true }; c.border = border;
          const v = ws.getCell(r + 1, i + 1); v.value = spec.docCount ?? spec.rows.length;
          v.font = F(24, true, sec.color); v.alignment = { horizontal: "center", vertical: "middle" }; v.border = border; v.fill = fill("F8FAFC");
        });
        ws.getRow(r).height = 28; ws.getRow(r + 1).height = 40; r += 2;
      };
      cardRow(specs.slice(0, 6));
      cardRow(specs.slice(6));
      r++;

      if (cashRows.length) {
        ws.mergeCells(r, 1, r, 6); const h3 = ws.getCell(r, 1); h3.value = "🏦 موجودی دخل بعد از آخرین تراکنش";
        h3.font = F(16, true, NAVY); h3.border = { bottom: { style: "medium", color: { argb: "D97706" } } };
        ws.getRow(r).height = 26; r++;
        cashRows.forEach(c => {
          const a1 = ws.getCell(r, 1); a1.value = c.label; a1.font = F(16, true, NAVY); a1.fill = fill("FEF3C7"); a1.border = border; a1.alignment = { horizontal: "right", vertical: "middle" };
          const a2 = ws.getCell(r, 2); a2.value = c.balance; a2.numFmt = "#,##0.00"; a2.font = F(18, true, c.balance < 0 ? "DC2626" : "059669"); a2.fill = fill("FEF3C7"); a2.border = border; a2.alignment = { horizontal: "center", vertical: "middle" };
          const a3 = ws.getCell(r, 3); a3.value = c.currency; a3.font = F(14, true, NAVY); a3.fill = fill("FEF3C7"); a3.border = border; a3.alignment = { horizontal: "center", vertical: "middle" };
          ws.getRow(r).height = 32; r++;
        });
      }

      specs.forEach(({ spec }) => addSpecSheet(wb, spec, meta));
      const buf = await wb.xlsx.writeBuffer();
      saveAs(new Blob([buf], { type: "application/octet-stream" }), `گزارش-جامع-ژورنال-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch { setErr("خطا در ساخت فایل Excel"); }
    setBusy("");
  };

  // ─────────── ظاهر ───────────
  const card: React.CSSProperties = { background: dark ? "rgba(255,255,255,0.03)" : "#fff", border: "1px solid " + T.cardBorder, borderRadius: 16, padding: 18 };
  const chip = (active: boolean, c = "#3b82f6"): React.CSSProperties => ({
    display: "flex", alignItems: "center", gap: 6, padding: "9px 16px", borderRadius: 10, cursor: "pointer", fontSize: 12.5, fontWeight: 700, border: "none",
    fontFamily: "'Vazirmatn','Segoe UI',sans-serif", background: active ? c : dark ? "rgba(255,255,255,0.06)" : "#f1f5f9", color: active ? "#fff" : T.subtext,
    boxShadow: active ? `0 3px 12px ${c}50` : "none",
  });

  const cellText = (c: RCol, v: any) => {
    if (v === null || v === undefined || v === "") return "—";
    if (c.kind === "date") return jal(v);
    if (c.kind === "money") return nf2(Number(v));
    if (c.kind === "qty") return nf(Number(v), 4);
    if (c.kind === "rate") return nf(Number(v), 6);
    if (c.kind === "int") return nf(Number(v), 0);
    return String(v);
  };

  return (
    <div style={{ fontFamily: "'Vazirmatn','Segoe UI',sans-serif", direction: "rtl" }}>
      {/* هدر */}
      <div style={{ borderRadius: 18, padding: "22px 26px", marginBottom: 16, background: "linear-gradient(120deg,#1e293b,#334155 60%,#2563eb)", color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800 }}>📒 گزارش جامع ژورنال</div>
          <div style={{ fontSize: 12, opacity: 0.85, marginTop: 4 }}>همه‌ی تراکنش‌های نهایی‌شده با جزئیات کامل</div>
        </div>
        {data && <div style={{ background: "rgba(255,255,255,0.15)", padding: "8px 18px", borderRadius: 20, fontSize: 12.5, fontWeight: 700 }}>دوره: {periodLabel}</div>}
      </div>

      {/* انتخاب دوره */}
      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <Calendar size={16} color={T.subtext} />
          <button style={chip(mode === "today", "#10b981")} onClick={() => setMode("today")}>امروز</button>
          <button style={chip(mode === "yesterday", "#f59e0b")} onClick={() => setMode("yesterday")}>دیروز</button>
          <button style={chip(mode === "all", "#8b5cf6")} onClick={() => setMode("all")}>همه‌ی تاریخ‌ها</button>
          <button style={chip(mode === "custom", "#3b82f6")} onClick={() => setMode("custom")}>از تاریخ … تا تاریخ …</button>
        </div>
        {mode === "custom" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 14, maxWidth: 560 }}>
            <div><div style={{ fontSize: 12, color: T.subtext, marginBottom: 4 }}>از تاریخ</div><PersianDatePicker value={dateFrom} onChange={setDateFrom} /></div>
            <div><div style={{ fontSize: 12, color: T.subtext, marginBottom: 4 }}>تا تاریخ</div><PersianDatePicker value={dateTo} onChange={setDateTo} /></div>
          </div>
        )}
        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <button onClick={load} disabled={loading} style={{ ...chip(true, "#2563eb"), padding: "11px 24px", opacity: loading ? 0.7 : 1 }}>
            {loading ? <Loader2 size={15} className="spin" /> : <RefreshCw size={15} />} نمایش گزارش
          </button>
          {data && (
            <>
              <button onClick={doPdf} disabled={!!busy} style={{ ...chip(true, "#dc2626"), padding: "11px 24px" }}>{busy === "pdf" ? <Loader2 size={15} className="spin" /> : <FileText size={15} />} PDF</button>
              <button onClick={doExcel} disabled={!!busy} style={{ ...chip(true, "#059669"), padding: "11px 24px" }}>{busy === "xls" ? <Loader2 size={15} className="spin" /> : <FileSpreadsheet size={15} />} Excel</button>
            </>
          )}
        </div>
        {err && <div style={{ marginTop: 12, color: "#ef4444", fontSize: 12.5, fontWeight: 700 }}>{err}</div>}
      </div>

      {!data && !loading && <div style={{ ...card, textAlign: "center", color: T.subtext, padding: 40 }}>دوره را انتخاب کنید و «نمایش گزارش» را بزنید.</div>}
      {loading && <div style={{ ...card, textAlign: "center", color: T.subtext, padding: 40 }}>در حال بارگذاری...</div>}

      {data && (
        <>
          {/* کارت‌های تعداد */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 16 }}>
            {specs.map(({ sec, spec }) => (
              <div key={sec.key} style={{ ...card, padding: "14px 16px", borderRight: `5px solid #${sec.color}` }}>
                <div style={{ fontSize: 12, color: T.subtext, fontWeight: 700 }}>{sec.icon} {sec.label}</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: `#${sec.color}`, marginTop: 4 }}>{spec.docCount ?? spec.rows.length}</div>
              </div>
            ))}
            <div style={{ ...card, padding: "14px 16px", borderRight: "5px solid #1e293b", background: dark ? "rgba(255,255,255,0.06)" : "#f8fafc" }}>
              <div style={{ fontSize: 12, color: T.subtext, fontWeight: 700 }}>🔢 جمع اسناد</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: T.text, marginTop: 4 }}>{totalCount}</div>
            </div>
          </div>

          {/* جمع هر ارز */}
          {allSum.length > 0 && (
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 12 }}>📊 جمع‌ها</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 10 }}>
                {allSum.map((r, i) => (
                  <div key={i} style={{ borderRadius: 12, padding: "11px 14px", background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc", border: `1px solid ${T.cardBorder}`, borderRight: `4px solid #${r.color}` }}>
                    <div style={{ fontSize: 11, color: T.subtext, fontWeight: 700 }}>{r.section} — {r.metric}</div>
                    <div style={{ fontSize: 17, fontWeight: 800, color: `#${r.color}`, direction: "ltr", textAlign: "right", marginTop: 3 }}>{nf2(r.sum)} <span style={{ fontSize: 11 }}>{r.cur}</span></div>
                    <div style={{ fontSize: 11.5, color: T.subtext, marginTop: 2 }}>{r.n} مورد</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* موجودی دخل */}
          {cashRows.length > 0 && (
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: T.text, marginBottom: 12 }}>🏦 موجودی دخل بعد از آخرین تراکنش</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 12 }}>
                {cashRows.map((c: any) => (
                  <div key={c.code} style={{ borderRadius: 14, padding: "16px 20px", border: `1px solid ${T.cardBorder}`, background: dark ? "rgba(255,255,255,0.04)" : "#f8fafc" }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.subtext }}>{c.label}</div>
                    <div style={{ fontSize: 28, fontWeight: 800, direction: "ltr", textAlign: "right", color: c.balance < 0 ? "#dc2626" : "#059669" }}>{nf2(c.balance)} <span style={{ fontSize: 15 }}>{c.currency}</span></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* مفاد و ضرر تبادله */}
          {data.fx.length > 0 && (
            <div style={{ ...card, marginBottom: 16 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.text, marginBottom: 10 }}>💱 مفاد و ضرر تبادله (تفاوت گرد کردن نرخ)</div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {data.fx.map((x: any) => (
                  <div key={x.currency} style={{ borderRadius: 12, padding: "10px 16px", border: `1px solid ${T.cardBorder}`, direction: "ltr" }}>
                    <b>{x.currency}</b> · سود <span style={{ color: "#059669", fontWeight: 700 }}>{nf2(x.gain)}</span> · زیان <span style={{ color: "#dc2626", fontWeight: 700 }}>{nf2(x.loss)}</span> · خالص <b style={{ color: x.net < 0 ? "#dc2626" : "#059669" }}>{nf2(x.net)}</b>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* جدول هر بخش */}
          {specs.map(({ sec, spec }) => {
            const isOpen = open[sec.key] ?? false;
            const count = spec.docCount ?? spec.rows.length;
            const sums = summaryRows(sec, spec);
            return (
              <div key={sec.key} style={{ ...card, padding: 0, marginBottom: 14, overflow: "hidden" }}>
                <div onClick={() => setOpen(o => ({ ...o, [sec.key]: !isOpen }))} style={{ cursor: "pointer", padding: "14px 18px", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", borderRight: `5px solid #${sec.color}` }}>
                  <span style={{ fontSize: 15, fontWeight: 800, color: T.text }}>{sec.icon} {sec.label}</span>
                  <span style={{ fontSize: 11, color: "#fff", background: `#${sec.color}`, borderRadius: 20, padding: "2px 12px", fontWeight: 700 }}>{count} سند</span>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginRight: "auto", direction: "ltr" }}>
                    {sums.slice(0, 6).map((r, i) => (
                      <span key={i} style={{ fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 8, border: `1px solid #${sec.color}55`, color: `#${sec.color}` }}>{nf2(r.sum)} {r.cur}</span>
                    ))}
                  </div>
                  {isOpen ? <ChevronUp size={18} color={T.subtext} /> : <ChevronDown size={18} color={T.subtext} />}
                </div>
                {isOpen && (
                  <div style={{ maxHeight: "60vh", overflow: "auto", borderTop: `1px solid ${T.cardBorder}` }}>
                    {spec.rows.length === 0 ? <div style={{ padding: 30, textAlign: "center", color: T.subtext }}>در این دوره موردی نیست</div> : (
                      <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: "100%" }}>
                        <thead>
                          <tr>{spec.cols.map(c => <th key={c.key} style={{ position: "sticky", top: 0, background: dark ? "#1e293b" : "#f1f5f9", color: T.subtext, padding: "9px 10px", whiteSpace: "nowrap", borderBottom: `1px solid ${T.cardBorder}`, fontWeight: 700 }}>{c.header}</th>)}</tr>
                        </thead>
                        <tbody>
                          {spec.rows.map((row, i) => (
                            <tr key={i} style={{ background: i % 2 ? (dark ? "rgba(255,255,255,0.02)" : "#fafbfc") : "transparent" }}>
                              {spec.cols.map(c => {
                                const v = row[c.key];
                                const isNum = c.kind === "money" || c.kind === "qty" || c.kind === "rate";
                                return (
                                  <td key={c.key} style={{ padding: "7px 10px", borderBottom: `1px solid ${T.cardBorder}`, color: c.kind === "badge" ? `#${sec.color}` : T.text, fontWeight: c.kind === "badge" ? 800 : 400, textAlign: c.kind === "wrap" || c.align === "right" ? "right" : "center", direction: isNum ? "ltr" : undefined, whiteSpace: c.kind === "wrap" ? "normal" : "nowrap", minWidth: c.kind === "wrap" ? 160 : undefined }}>
                                    {c.kind === "link" && v ? <a href={fileUrl(String(v))} target="_blank" rel="noreferrer" style={{ color: "#3b82f6", fontWeight: 700 }}>مشاهده</a> : cellText(c, v)}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}
